import React, { useState, useEffect } from 'react';
import { Calculator, CheckCircle2, XCircle, Info, AlertCircle, Table as TableIcon, ArrowLeft } from 'lucide-react';

// Replace with your Level 1 table asset path
import tableImage from "../assets/AcceptanceLevel1.png";
import { useNavigate } from 'react-router-dom';

const AcceptanceCriteriaLevel1 = () => {
    const [thickness, setThickness] = useState('');
    const [length, setLength] = useState('');
    const [height, setHeight] = useState('');
    const [defectType, setDefectType] = useState('embedded');
    const [result, setResult] = useState(null);
    const [showTableImage, setShowTableImage] = useState(false);

    const navigate = useNavigate();
    const calculateAcceptance = () => {
        const t = parseFloat(thickness);
        const l = parseFloat(length);
        const h = parseFloat(height);

        if (isNaN(t) || isNaN(l) || isNaN(h)) return;

        let criteria = null;

        // Logic updated for Table 2 — Level 1 Acceptance Criteria
        if (t > 5 && t <= 15) {
            criteria = { lMax: 0.75 * t, h3: 1.5, h2: 2, h1: 1 };
        } else if (t > 15 && t <= 50) {
            criteria = { lMax: 0.75 * t, h3: 2, h2: 3, h1: 1 };
        } else if (t > 50 && t <= 100) {
            criteria = { lMax: 40, h3: 2.5, h2: 4, h1: 2 };
        } else if (t > 100) {
            criteria = { lMax: 50, h3: 3, h2: 5, h1: 2 };
        }

        if (!criteria) {
            setResult({ status: 'Error', message: 'Thickness must be > 6mm' });
            // alert("Thickness must be > 6mm")
            return;
        }

        const maxHForCurrentType = defectType === 'embedded' ? criteria.h2 : criteria.h3;
        let isAccepted = false;
        let reason = "";

        // Evaluation Logic
        if (l <= criteria.lMax) {
            if (h <= maxHForCurrentType) {
                isAccepted = true;
                reason = `Length (${l} mm) is within l_max (${criteria.lMax.toFixed(2)} mm). Height is within the limit for ${defectType} indications (${maxHForCurrentType} mm).`;
            } else {
                reason = `Height (${h} mm) exceeds the allowed ${maxHForCurrentType} mm for ${defectType} indications.`;
            }
        } else {
            if (h <= criteria.h1) {
                isAccepted = true;
                reason = `Length (${l} mm) exceeds l_max (${criteria.lMax.toFixed(2)} mm), but height is within the reduced h1 limit (${criteria.h1} mm).`;
            } else {
                reason = `Length exceeds l_max and height (${h} mm) exceeds the reduced h1 limit (${criteria.h1} mm).`;
            }
        }

        setResult({
            status: isAccepted ? 'ACCEPTED' : 'REJECTED',
            reason: reason,
            lMax: criteria.lMax.toFixed(2),
            allowedH: l <= criteria.lMax ? maxHForCurrentType : criteria.h1
        });
    };

    useEffect(() => {
        if (thickness && length && height) {
            calculateAcceptance();
        } else {
            setResult(null);
        }
    }, [thickness, length, height, defectType]);

    return (
        <div className="min-h-screen bg-[#f8fafc] py-12 px-4 font-sans text-slate-900">
            <div className="max-w-4xl mx-auto">

                <header className="mb-12">
                    {/* Top Utility Row */}
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-6 border-b border-slate-200">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate('/resources/iso')}
                                className="group flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400 hover:text-cyan-600 transition-colors"
                            >
                                <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
                                Back to ISO Hub
                            </button>
                            <span className="text-slate-200">|</span>
                            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 rounded-full">
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            </div>
                        </div>

                        {/* <div className="hidden sm:flex items-center gap-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
      <span>Ref: ISO 11666</span>
      <span className="w-1 h-1 bg-slate-300 rounded-full" />
      <span>Rev: 2026.01</span>
    </div> */}
                    </div>

                    {/* Main Title Area */}
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                        <div className="flex items-start gap-5">
                            <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-cyan-600 to-cyan-700 rounded-2xl shadow-xl shadow-cyan-100 flex items-center justify-center border border-cyan-500/20">
                                <Calculator className="text-white" size={32} strokeWidth={1.5} />
                            </div>
                            <div>
                                <h1 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight leading-none">
                                    Acceptance Level 1
                                </h1>
                                <p className="mt-2 text-slate-500 font-medium flex items-center gap-2">
                                    <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 rounded text-xs font-bold uppercase">Table 2</span>
                                </p>
                            </div>
                        </div>


                    </div>
                </header>

                <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden mb-8">
                    <div className="p-1 md:p-8">
                        <div className="flex flex-col gap-8">

                            {/* Inputs */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-4">
                              <div className="space-y-2">
  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1">
    Thickness (t)
  </label>
  <div className="relative">
    <input 
      type="number" 
      min="0"
      value={thickness} 
      onChange={(e) => {
        const val = e.target.value;
        // Prevent negative values at the state level
        if (val === '' || parseFloat(val) >= 0) {
          setThickness(val);
        }
      }} 
      className={`w-full pl-4 pr-12 py-3 bg-slate-50 border rounded-xl outline-none transition-all font-semibold ${
        thickness !== '' && parseFloat(thickness) <= 5 
          ? 'border-rose-500 ring-2 ring-rose-100' 
          : 'border-slate-200 focus:ring-2 focus:ring-cyan-500'
      }`} 
      placeholder="0.00" 
    />
    <span className="absolute right-4 top-3.5 text-slate-400 text-sm">mm</span>
  </div>

  {/* Inline Validation Message */}
  {thickness !== '' && parseFloat(thickness) <= 5 && (
    <div className="flex items-center gap-1.5 px-1 animate-in fade-in slide-in-from-top-1">
      <AlertCircle className="text-rose-500" size={12} />
      <p className="text-[10px] font-bold text-rose-600 uppercase tracking-tight">
        Outside ISO Range: Must be &gt; 6mm
      </p>
    </div>
  )}
</div>

                                <div className="space-y-2">
                                    <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1">Defect Length (l)</label>
                                    <div className="relative">
                                        <input type="number" min="0"
                                            value={length} onChange={(e) => setLength(e.target.value)} className="w-full pl-4 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-cyan-500 outline-none transition-all font-semibold" placeholder="0.00" />
                                        <span className="absolute right-4 top-3.5 text-slate-400 text-sm">mm</span>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1">Defect Height (h)</label>
                                    <div className="relative">
                                        <input type="number" min="0"
                                            value={height} onChange={(e) => setHeight(e.target.value)} className="w-full pl-4 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-cyan-500 outline-none transition-all font-semibold" placeholder="0.00" />
                                        <span className="absolute right-4 top-3.5 text-slate-400 text-sm">mm</span>
                                    </div>
                                </div>
                            </div>

                            {/* Toggle */}
                            <div className="px-4">
                                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 ml-1">Discontinuity Location</label>
                                <div className="flex p-1 bg-slate-100 rounded-2xl w-full max-w-md mx-auto shadow-inner">
                                    <button type="button" onClick={() => setDefectType('surface')} className={`flex-1 py-3 px-6 rounded-xl text-sm font-bold transition-all duration-200 ${defectType === 'surface' ? 'bg-white text-cyan-600 shadow-md scale-[1.02]' : 'text-slate-500'}`}>
                                        Surface (h3)
                                    </button>
                                    <button type="button" onClick={() => setDefectType('embedded')} className={`flex-1 py-3 px-6 rounded-xl text-sm font-bold transition-all duration-200 ${defectType === 'embedded' ? 'bg-white text-cyan-600 shadow-md scale-[1.02]' : 'text-slate-500'}`}>
                                        Embedded (h2)
                                    </button>
                                </div>
                            </div>

                            {/* Result */}
                            <div className="px-4 pb-4">
                                {result ? (
                                    <div className={`rounded-2xl p-6 border ${result.status === "ACCEPTED" ? "bg-emerald-50 border-emerald-100" : "bg-rose-50 border-rose-100"}`}>
                                        <div className="flex items-start justify-between mb-4">

                                            <div className="flex items-center gap-4">
                                                {result.status === "ACCEPTED" ? (
                                                    <CheckCircle2 className="text-emerald-600" size={32} />
                                                ) : (
                                                    <XCircle className="text-rose-600" size={32} />
                                                )}

                                                <div>
                                                    <span
                                                        className={`text-xs font-bold uppercase tracking-widest ${result.status === "ACCEPTED"
                                                                ? "text-emerald-700"
                                                                : "text-rose-700"
                                                            }`}
                                                    >
                                                        Inspection Result
                                                    </span>

                                                    <h2
                                                        className={`text-2xl font-black ${result.status === "ACCEPTED"
                                                                ? "text-emerald-800"
                                                                : "text-rose-800"
                                                            }`}
                                                    >
                                                        {result.status}
                                                    </h2>
                                                </div>
                                            </div>

                                            {/* Defect Type Badge */}
                                            <div className="text-right">
                                                <p className="text-[10px] uppercase font-bold text-slate-500">
                                                    Defect Type
                                                </p>
                                                <p className="text-sm font-bold text-slate-800">
                                                    {defectType}
                                                </p>
                                            </div>

                                        </div>
                                        <div className="grid grid-cols-2 gap-4 bg-white/50 rounded-xl p-4 mb-4">
                                            <div>
                                                <p className="text-[10px] text-slate-500 uppercase font-bold">Max Allowed Length (l_max)</p>
                                                <p className="text-lg font-bold text-slate-800">{result.lMax} mm</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] text-slate-500 uppercase font-bold">Max Allowed Height</p>
                                                <p className="text-lg font-bold text-slate-800">{result.allowedH} mm</p>
                                            </div>
                                        </div>
                                        <p className="text-sm text-slate-600 flex items-start gap-2 leading-relaxed">
                                            <Info size={16} className="mt-0.5 shrink-0" />
                                            <span><strong>Explanation:</strong> {result.reason}</span>
                                        </p>
                                    </div>
                                ) : (
                                    <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-2xl">
                                        <AlertCircle className="mx-auto text-slate-300 mb-2" />
                                        <p className="text-slate-400 text-sm font-medium">Enter values to see Level 1 results.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Level 1 Reference Table UI */}
                <div className="space-y-4">
                    <div className="flex items-center gap-2 px-2">
                        <TableIcon size={18} className="text-slate-400" />
                        <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wide">Reference Table 2 (Level 1)</h3>
                        <button onClick={() => setShowTableImage(true)} className="ml-auto px-3 py-1.5 text-xs font-semibold bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition">
                            View Image
                        </button>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden overflow-x-auto">
                        <table className="w-full text-left text-sm border-collapse">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200">
                                    <th className="p-4 font-bold text-slate-700 border-r">Thickness (t)</th>
                                    <th className="p-4 font-bold text-slate-700 border-r">l_max</th>
                                    <th className="p-4 font-bold text-slate-700 border-r">Surface (h3)</th>
                                    <th className="p-4 font-bold text-slate-700 border-r">Embedded (h2)</th>
                                    <th className="p-4 font-bold text-slate-700">If l &gt; l_max (h1)</th>
                                </tr>
                            </thead>
                            <tbody className="text-slate-600 divide-y divide-slate-100">
                                {[
                                    { range: "6 < t ≤ 15", l: "0,75t", h3: 1.5, h2: 2, h1: 1, active: thickness > 5 && thickness <= 15 },
                                    { range: "15 < t ≤ 50", l: "0,75t", h3: 2, h2: 3, h1: 1, active: thickness > 15 && thickness <= 50 },
                                    { range: "50 < t ≤ 100", l: "40", h3: 2.5, h2: 4, h1: 2, active: thickness > 50 && thickness <= 100 },
                                    { range: "t > 100", l: "50", h3: 3, h2: 5, h1: 2, active: thickness > 100 }
                                ].map((row, idx) => (
                                    <tr key={idx} className={row.active ? "bg-cyan-50" : ""}>
                                        <td className="p-4 font-medium border-r">{row.range}</td>
                                        <td className="p-4 border-r">{row.l}</td>
                                        <td className="p-4 border-r text-center">{row.h3}</td>
                                        <td className="p-4 border-r text-center">{row.h2}</td>
                                        <td className="p-4 text-center font-bold text-cyan-600">{row.h1}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Info Section */}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-2">

                    <div className="flex gap-3 p-4 bg-white border border-slate-200 rounded-xl">
                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center font-bold text-xs">
                            A
                        </div>

                        <p className="text-xs text-slate-500">
                            If the defect length (l) is less than or equal to l_max,
                            evaluate the defect using <strong>h2</strong> or <strong>h3</strong>.
                        </p>
                    </div>


                    <div className="flex gap-3 p-4 bg-white border border-slate-200 rounded-xl">
                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center font-bold text-xs">
                            B
                        </div>

                        <p className="text-xs text-slate-500">
                            If the defect length (l) exceeds l_max, the defect height must
                            not exceed the reduced <strong>h1</strong> limit.
                        </p>
                    </div>

                </div>

                {/* Modal Viewer */}
                {showTableImage && (
                    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowTableImage(false)}>
                        <div className="bg-white rounded-xl max-w-4xl w-full p-4 relative" onClick={e => e.stopPropagation()}>
                            <button onClick={() => setShowTableImage(false)} className="absolute top-3 right-3 text-slate-500">✕</button>
                            <h4 className="text-lg font-bold mb-4">Original Table 2 — Level 1</h4>
                            <img src={tableImage} alt="Level 1 Criteria" className="w-full rounded-lg border" />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AcceptanceCriteriaLevel1;